/**
 * CampanhasPage — F4-b: superfície de operador da Raiz de Tarefa/Campanha.
 *
 * Rota: /campanhas. Gated `proactive-outbound-campaign` (opt-in) — gate próprio
 * na página além da rota. Três superfícies (spec §4.4): lista, criação (modal),
 * monitor/revisão-fecho. O fecho é HUMANO (nunca auto-close); a plataforma só
 * leva a `awaiting_review`. Sobre os endpoints da F4-a (gated OFF ⇒ inerte).
 */
import { CapabilityLockedBanner } from '@lume/ui-components';
import { ArrowLeft, Megaphone, Plus } from 'lucide-react';
import { useState } from 'react';

import {
  CAMPAIGN_STATUS_BADGE,
  CAMPAIGN_STATUS_LABEL,
} from '../features/outbound-campaigns/campaign-adapter';
import { CampaignAggregateView } from '../features/outbound-campaigns/CampaignAggregateView';
import { CampaignCreateModal } from '../features/outbound-campaigns/CampaignCreateModal';
import {
  useCancelOutboundCampaign,
  useCloseOutboundCampaign,
  useOutboundCampaigns,
  useOutboundCampaignState,
} from '../features/outbound-campaigns/hooks/use-outbound-campaigns';
import { useCapability } from '../hooks/use-capability';

import type { OutboundCampaignRootWire, OutboundTaskRootStatusWire } from '../lib/types';
import type { JSX } from 'react';

export function CampanhasPage(): JSX.Element {
  const canUse = useCapability('proactive-outbound-campaign');
  if (!canUse) {
    return (
      <div className="flex h-full flex-col p-6">
        <CapabilityLockedBanner
          capability="proactive-outbound-campaign"
          tierMin="professional"
          fallbackMessage="As campanhas em lote não estão habilitadas para este tenant. Fale com o administrador para ativá-las."
        />
      </div>
    );
  }
  return <CampanhasPageInner />;
}

function CampanhasPageInner(): JSX.Element {
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <header className="flex items-start justify-between gap-3 border-b border-border-subtle bg-surface px-6 py-4">
        <div className="flex items-start gap-3">
          <Megaphone size={22} aria-hidden className="mt-0.5 text-text-secondary" />
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-text-secondary">
              Tarefa em lote
            </div>
            <h1 className="text-xl font-semibold text-text-primary">Campanhas</h1>
          </div>
        </div>
        {selectedId === null && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3.5 py-2 text-sm font-medium text-white"
          >
            <Plus size={14} aria-hidden />
            Nova campanha
          </button>
        )}
      </header>

      <div className="px-6 py-6">
        {selectedId === null ? (
          <CampaignList onSelect={setSelectedId} />
        ) : (
          <CampaignDetail id={selectedId} onBack={() => setSelectedId(null)} />
        )}
      </div>

      <CampaignCreateModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

function StatusBadge({ status }: { readonly status: OutboundTaskRootStatusWire }): JSX.Element {
  return (
    <span className={`badge ${CAMPAIGN_STATUS_BADGE[status]}`}>
      {CAMPAIGN_STATUS_LABEL[status]}
    </span>
  );
}

function CampaignList({ onSelect }: { readonly onSelect: (id: string) => void }): JSX.Element {
  const { data, isLoading, isError } = useOutboundCampaigns(true);

  if (isLoading) return <p className="text-sm text-text-secondary">Carregando campanhas…</p>;
  if (isError)
    return (
      <p role="alert" className="text-sm text-red-600">
        Falha ao carregar as campanhas.
      </p>
    );
  const items = data?.items ?? [];
  if (items.length === 0)
    return (
      <div className="rounded-lg border border-border-subtle bg-surface p-8 text-center">
        <p className="text-sm text-text-secondary">
          Nenhuma campanha ainda. Crie uma para disparar N conversas irmãs a partir de uma tarefa.
        </p>
      </div>
    );

  return (
    <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {['Campanha', 'Situação', 'Progresso', 'Criada'].map((h) => (
              <th
                key={h}
                className="border-b border-border-subtle px-4 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text-secondary"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((c: OutboundCampaignRootWire) => {
            const agg = c.aggregate;
            const progress =
              agg !== null
                ? `${agg.progress.settled}/${agg.progress.total} assentadas`
                : `${c.audienceCount} alvos`;
            return (
              <tr
                key={c.id}
                onClick={() => onSelect(c.id)}
                className="cursor-pointer hover:bg-app-bg"
              >
                <td className="border-b border-border-subtle px-4 py-3 font-medium text-text-primary">
                  {c.title}
                </td>
                <td className="border-b border-border-subtle px-4 py-3">
                  <StatusBadge status={c.status} />
                </td>
                <td className="border-b border-border-subtle px-4 py-3 text-text-secondary">
                  {progress}
                </td>
                <td className="border-b border-border-subtle px-4 py-3 font-mono text-xs text-text-secondary">
                  {formatDate(c.createdAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CampaignDetail({
  id,
  onBack,
}: {
  readonly id: string;
  readonly onBack: () => void;
}): JSX.Element {
  const { data, isLoading, isError } = useOutboundCampaignState(id, true);
  const { close, isPending: closing } = useCloseOutboundCampaign();
  const { cancel, isPending: cancelling } = useCancelOutboundCampaign();
  // Ações que ENCERRAM a campanha pedem confirmação (spec §4.2). Two-step inline
  // (nada de window.confirm — regra do app).
  const [pendingAction, setPendingAction] = useState<'cancel' | 'force' | null>(null);

  if (isLoading) return <p className="text-sm text-text-secondary">Carregando a campanha…</p>;
  if (isError || data === undefined)
    return (
      <div className="flex flex-col gap-3">
        <BackLink onBack={onBack} />
        <p role="alert" className="text-sm text-red-600">
          Falha ao carregar a campanha.
        </p>
      </div>
    );

  const { root, aggregate } = data;
  const isTerminal = root.status === 'closed' || root.status === 'cancelled';
  const canReview = root.status === 'awaiting_review';
  const canForceClose = root.status === 'active' || root.status === 'dispatching';

  async function handleClose(force: boolean): Promise<void> {
    await close(id, root.version, force);
  }
  async function handleCancel(): Promise<void> {
    await cancel(id, root.version);
  }

  return (
    <div className="flex flex-col gap-4">
      <BackLink onBack={onBack} />

      <div className="rounded-lg border border-border-subtle bg-surface p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-text-primary">{root.title}</h2>
              <StatusBadge status={root.status} />
            </div>
            <span className="text-sm text-text-secondary">
              {root.audienceCount} conversa(s) · modo {root.mode}
            </span>
          </div>
          {!isTerminal && pendingAction === null && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPendingAction('cancel')}
                disabled={cancelling}
                className="rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm font-medium text-text-secondary disabled:opacity-50"
              >
                Cancelar
              </button>
              {canReview && (
                <button
                  type="button"
                  onClick={() => void handleClose(false)}
                  disabled={closing}
                  className="rounded-md bg-accent px-3.5 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  Encerrar campanha
                </button>
              )}
              {!canReview && canForceClose && (
                <button
                  type="button"
                  onClick={() => setPendingAction('force')}
                  disabled={closing}
                  className="rounded-md border border-border-subtle bg-surface px-3.5 py-2 text-sm font-medium text-text-secondary disabled:opacity-50"
                  title="Encerrar mesmo com conversas em curso"
                >
                  Forçar encerramento
                </button>
              )}
            </div>
          )}
          {!isTerminal && pendingAction !== null && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-text-secondary">
                {pendingAction === 'cancel'
                  ? 'Cancelar a campanha? As conversas pendentes não serão disparadas.'
                  : 'Encerrar com conversas ainda em curso?'}
              </span>
              <button
                type="button"
                onClick={() =>
                  void (pendingAction === 'cancel' ? handleCancel() : handleClose(true)).then(() =>
                    setPendingAction(null),
                  )
                }
                disabled={closing || cancelling}
                className="rounded-md bg-accent px-3.5 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                Confirmar
              </button>
              <button
                type="button"
                onClick={() => setPendingAction(null)}
                className="rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm font-medium text-text-secondary"
              >
                Voltar
              </button>
            </div>
          )}
        </div>
      </div>

      {canReview && (
        <div
          role="status"
          className="rounded-md border px-4 py-3.5 text-sm"
          style={{
            background: 'var(--warning-bg)',
            borderColor: 'var(--warning-border)',
            color: 'var(--warning-fg)',
          }}
        >
          Condição de fecho atingida — as conversas assentaram ou o prazo venceu. Revise o resultado
          e encerre. Nada é fechado automaticamente.
        </div>
      )}

      <CampaignAggregateView aggregate={aggregate} />

      <p className="text-xs text-text-secondary">
        As conversas roteadas para resolução e em andamento são acompanhadas individualmente pela
        Inbox.
      </p>
    </div>
  );
}

function BackLink({ onBack }: { readonly onBack: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-text-secondary hover:text-text-primary"
    >
      <ArrowLeft size={14} aria-hidden />
      Todas as campanhas
    </button>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
